import { Selector } from '@/components';
import { MasterTranslation } from '@/constants';
import { useGlobalState } from '@/hooks';
import { useCarriersContext, useShipmentDetailsContext } from '@/modules/pmt/context';
import { updateShipmentCarrier } from '@/modules/pmt/fetchers';
import { CarrierDropdownState, type ICarrier, type ICarrierByParcel } from '@/modules/pmt/interfaces';
import { gtmUserSuggest } from '@/utils';
import { useStatsigClient } from '@statsig/react-bindings';
import produce from 'immer';
import { type Dispatch, type FC, type SetStateAction, useCallback, useMemo, useState } from 'react';
import { TrackingCarrierSelectorWrapper } from './TrackingCarrierSelector.styles';

interface ITrackingCarrierSelector {
  disabled?: boolean;
  carrierByParcel: ICarrierByParcel;
  setCarrierByParcel: Dispatch<SetStateAction<ICarrierByParcel>>;
}

export const TrackingCarrierSelector: FC<ITrackingCarrierSelector> = ({
  disabled,
  carrierByParcel,
  setCarrierByParcel,
}) => {
  const { t, firestoreUuid } = useGlobalState();
  const { carriersData } = useCarriersContext();
  const { currentShipment, currentShipmentDocId } = useShipmentDetailsContext();
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);
  const { logEvent } = useStatsigClient();

  const currentCarrier = useMemo(() => {
    // return the current carrier with state is Scarping inside carrierByParcel[selectedShipmentDocId]
    if (!currentShipmentDocId || !carrierByParcel || !carrierByParcel[currentShipmentDocId]) return null;
    return carrierByParcel[currentShipmentDocId][CarrierDropdownState.Selected] || null;
  }, [carrierByParcel, currentShipmentDocId]);

  const handleUpdateShipmentWithCarrier = useCallback(
    (carrierId: string) => {
      if (!firestoreUuid || !currentShipmentDocId) return;

      const trackingNumber = currentShipment?.data?.parcel?.parcelId;
      if (!trackingNumber) return;

      const updatedShipmentData: Parameters<typeof updateShipmentCarrier>[0] = {
        firestoreUuid,
        firestoreDocId: currentShipmentDocId,
        trackingNumber,
      };

      if (carrierId) {
        updatedShipmentData.carrierId = carrierId;
      }
      updateShipmentCarrier(updatedShipmentData);
    },
    [currentShipment, firestoreUuid, currentShipmentDocId],
  );

  const handleFindParcel = useCallback(() => {
    if (!currentShipmentDocId || !currentCarrier?.id) return;
    gtmUserSuggest(currentCarrier.name);
    logEvent('user_suggest', currentCarrier.id, {
      carrier_name: currentCarrier.name,
      source: 'tracking_pending_page',
    });
    handleUpdateShipmentWithCarrier(currentCarrier.id);
    setCarrierByParcel(
      produce((draft) => {
        if (!currentShipmentDocId) return;
        draft[currentShipmentDocId] = {
          ...draft[currentShipmentDocId],
          [CarrierDropdownState.Scraping]: currentCarrier,
        };
      }),
    );
  }, [currentCarrier, currentShipmentDocId, setCarrierByParcel, handleUpdateShipmentWithCarrier]);

  const handleSelectCarrier = (carrier: ICarrier | null) => {
    if (!currentShipmentDocId) return;
    setButtonDisabled(!carrier);
    setCarrierByParcel(
      produce((draft) => {
        if (!currentShipmentDocId || !carrier) return;
        draft[currentShipmentDocId] = {
          ...draft[currentShipmentDocId],
          [CarrierDropdownState.Selected]: carrier,
        };
      }),
    );
  };

  if (!carriersData?.length) return null;

  return (
    <TrackingCarrierSelectorWrapper>
      <Selector
        dropdownList={carriersData}
        onSelect={handleSelectCarrier}
        defaultSelectedItem={currentCarrier}
        placeholder={t(MasterTranslation.CarrierDropdownPlaceholder)}
        defaultLogo='/images/icons/parcel_logo.svg'
        disabled={disabled}
        disabledCustomSearch={!currentCarrier || disabled || buttonDisabled}
        onCustomSearchClicked={handleFindParcel}
        ariaLabel='Carrier options list box'
        inputAriaLabel='Search carriers in the list'
      />
    </TrackingCarrierSelectorWrapper>
  );
};
